import axios from 'axios';
import { $getStorage } from './storage';
import router from '~/router';

let headers = {
	'X-Requested-With': 'XMLHttpRequest',
	'Content-Type': 'application/json',
};

const $axios = axios.create({
	baseURL: import.meta.env.VITE_API_ENDPOINT,
	withCredentials: true,
	headers: headers,
});

let refreshPromise = null;

export { refreshPromise as pendingRefresh };

export function refreshSession() {
	const auth = useAuthStore();
	if (!refreshPromise) {
		refreshPromise = $axios
			.post(
				'/auths/refresh',
				{},
				{
					withCredentials: true,
					_isRefresh: true,
					headers: {
						'X-Refresh': '1',
						'X-Device-Id': $getStorage('device_id'),
					},
				},
			)
			.then((res) => {
				const token = res.data?.data?.token;
				if (!token) throw new Error('Refresh response missing token');
				auth.setAccessToken(token);
			})
			.catch((err) => {
				auth.logout();
				throw err;
			})
			.finally(() => {
				refreshPromise = null;
			});
	}
	return refreshPromise;
}

//Intercept Request
$axios.interceptors.request.use(async (config) => {
	const auth = useAuthStore();
	const response = useResponseStore();
	response.startRequest();
	if (!config._isRefresh && !auth.token && refreshPromise) {
		try { await refreshPromise; } catch (_e) {}
	}
	if (auth.token) config.headers['X-Authorization'] = 'Bearer ' + auth.token;
	if (auth.company?.id) config.headers['X-Company'] = auth.company?.id;
	if (auth.branch?.id) config.headers['X-Branch'] = auth.branch?.id;

	const deviceId = $getStorage('device_id');
	if (deviceId) config.headers['X-Device-Id'] = deviceId;

	//add locale
	config.headers['Accept-Language'] = $getStorage('locale') ?? 'id';
	return config;
});

$axios.interceptors.response.use(
	(response) => {
		const responseStore = useResponseStore();
		responseStore.endRequest();
		return response;
	},
	(error) => {
		const auth = useAuthStore();
		const response = useResponseStore();
		response.endRequest();
		if (error) {
			response.statusCode = error.response?.status ?? 0;
			if (error.response) {
				switch (error.response.status) {
					case 401:
						if (error.config?._isRefresh) {
							return Promise.reject(error.message);
						}
						if (!error.config?._retry) {
							error.config._retry = true;
							return refreshSession()
								.then(() => $axios(error.config))
								.catch((err) => Promise.reject(err.message));
						}
						if (document.location.pathname != '/auths/login')
							document.location.href = '/auths/login';
						return Promise.reject(error.message);
					case 403:
						response.setErrors({
							server: 'This action is unauthorized',
						});
						return Promise.reject(error.message);
					case 404:
						router.replace('/not-found');
						return Promise.reject(error.message);
					case 406:
						router.replace('/not-found');
						return Promise.reject(error.message);
					case 503:
						location.reload();
						return Promise.reject(error.message);
					case 422:
						if (error.response.data?.message) {
							if (
								typeof error.response.data.message === 'string'
							) {
								response.setErrors({
									server: error.response.data.message,
								});
							} else {
								response.setErrors(
									error.response.data.message,
								);
							}
						}
						return Promise.reject(error.message);
					default:
						if (error.response.status == 500) {
							if (!error.response.data) {
								response.setErrors({ server: error.message });
								return Promise.reject(error.message);
							}
							if (!error.response.data.message) {
								response.setErrors({ server: error.message });
								return Promise.reject(error.message);
							}
							if (!error.response.data.message.error) {
								response.setErrors({ server: error.message });
								return Promise.reject(error.message);
							}
							response.setErrors({
								server: error.response.data.message.error,
							});
							return Promise.reject(error.message);
						}
						break;
				}
				response.setErrors({
					server: error.response.data?.message ?? error.message,
				});
				return Promise.reject(error.message);
			}
			return Promise.reject(error.message);
		}
	},
);

export default $axios;
